/* Quizkosh — lightweight page-view analytics client.
 *
 * Isolated, dependency-free, non-blocking. Uses `sendBeacon` when
 * available so page loads are never delayed by the tracking request.
 *
 * Data is 100% anonymous:
 *   - visitor id (uuid, localStorage, persistent)
 *   - session id (uuid, sessionStorage, per-tab)
 *   - page path (query strings stripped)
 *   - referrer, UTM params, device / browser / OS (parsed server-side)
 *   - screen resolution + timezone (rough grouping only)
 * Never sends emails, names, quiz text, or auth tokens.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VID_KEY = "qz_vid";
const SID_KEY = "qz_sid";

function uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* noop */ }
  // Fallback (RFC 4122-ish, sufficient for anonymous IDs).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getVisitorId() {
  try {
    let v = window.localStorage.getItem(VID_KEY);
    if (!v) { v = uuid(); window.localStorage.setItem(VID_KEY, v); }
    return v;
  } catch (_) { return ""; }
}

function getSessionId() {
  try {
    let s = window.sessionStorage.getItem(SID_KEY);
    if (!s) { s = uuid(); window.sessionStorage.setItem(SID_KEY, s); }
    return s;
  } catch (_) { return ""; }
}

function readUtm() {
  try {
    const p = new URLSearchParams(window.location.search || "");
    const pick = (k) => (p.get(k) || "").slice(0, 200);
    return {
      source: pick("utm_source"),
      medium: pick("utm_medium"),
      campaign: pick("utm_campaign"),
      term: pick("utm_term"),
      content: pick("utm_content"),
    };
  } catch (_) {
    return { source: "", medium: "", campaign: "", term: "", content: "" };
  }
}

let _lastPath = "";
let _lastAt = 0;

/**
 * Record a page view. Safe to call from any effect — always fire-and-forget.
 * De-dupes rapid same-path calls within 800 ms to prevent double-counts
 * from React Strict Mode / component remounts.
 */
export function trackPageView(path) {
  try {
    if (!API) return;
    const now = Date.now();
    const p = (path || (typeof window !== "undefined" ? window.location.pathname : "/") || "/");
    if (p === _lastPath && now - _lastAt < 800) return;
    _lastPath = p; _lastAt = now;

    const body = JSON.stringify({
      path: p.slice(0, 500),
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      referrer: (typeof document !== "undefined" ? (document.referrer || "") : "").slice(0, 500),
      host: (typeof window !== "undefined" ? (window.location.hostname || "") : "").slice(0, 200),
      tz: (Intl && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : "") || "",
      screen: (typeof window !== "undefined" && window.screen)
        ? `${window.screen.width}x${window.screen.height}`.slice(0, 32) : "",
      utm: readUtm(),
    });
    const url = `${API}/analytics/visit`;

    // Prefer sendBeacon — guaranteed non-blocking, works on unload too.
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon(url, blob)) return;
      } catch (_) { /* fall through to fetch */ }
    }
    // Fallback — keepalive fetch, response ignored.
    try {
      fetch(url, {
        method: "POST",
        credentials: "omit",  // no cookies — this endpoint is public
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body,
      }).catch(() => {});
    } catch (_) { /* noop */ }
  } catch (_) {
    // Analytics MUST NEVER throw into the app.
  }
}

export default { trackPageView };
